/// <amd-module name="VSS/Features/SecurityUI/Models" />

import { IReadonlyObservableArray, IReadonlyObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { IVssPageContext } from "VSS/Platform/Context";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { getIdentityImageUrl } from "VSS/Platform/Identity";

import { RoleAccess, RoleAssignment, SecurityRole, UserRoleAssignmentRef } from "VSS/Features/SecurityUI/Contracts/SecurityRoles";
import * as Resources from "VSS/Features/SecurityUI/Resources";

import { IRoleAssignmentItemModel, IRoleAssignmentsModel, IRoleManamentPermissions } from "./ModelContracts";

export class RoleAssignmentItemModel implements IRoleAssignmentItemModel {
    private originalRoleAssignment: RoleAssignment;
    private roleAssignment: RoleAssignment;
    private deleted = new ObservableValue(false);
    private dirty = new ObservableValue(false);

    constructor(roleAssignment: RoleAssignment, isNew: boolean = false) {
        this.originalRoleAssignment = isNew ? ({ role: {} } as RoleAssignment) : roleAssignment;
        this.roleAssignment = { ...roleAssignment };
        this.checkDirty();
    }

    public get displayName(): string {
        return this.roleAssignment.identity.displayName || this.roleAssignment.identity.uniqueName;
    }

    public get role(): SecurityRole {
        return this.roleAssignment.role;
    }

    public set role(role: SecurityRole) {
        if (this.roleAssignment.role.identifier !== role.identifier) {
            this.roleAssignment.role = role;
            this.checkDirty();
        }
    }

    public get access(): RoleAccess {
        return this.roleAssignment.access;
    }

    public get accessDisplayName(): string {
        return this.roleAssignment.access === RoleAccess.Assigned ? Resources.AccessAssigned : Resources.AccessInherited;
    }

    public get userId(): string {
        return this.roleAssignment.identity.id;
    }

    public isDirty(): IReadonlyObservableValue<boolean> {
        return this.dirty;
    }

    public isDeleted(): IReadonlyObservableValue<boolean> {
        return this.deleted;
    }

    public delete(): void {
        this.deleted.value = true;
        this.originalRoleAssignment = { role: {} } as RoleAssignment;
        this.checkDirty();
    }

    public getIdentityImageUrl(pageContext: IVssPageContext): string {
        return getIdentityImageUrl(pageContext, this.roleAssignment.identity);
    }

    private checkDirty(): void {
        let isDirty = false;
        if (this.originalRoleAssignment.role.identifier !== this.roleAssignment.role.identifier) {
            isDirty = true;
        }

        this.dirty.value = isDirty;
    }
}

export class RoleAssignmentsModel implements IRoleAssignmentsModel {
    private originalRoleAssignments: RoleAssignment[];

    private dirty: ObservableValue<boolean>;
    private error: ObservableValue<Error | null>;
    private ready: ObservableValue<boolean>;
    private roleAssignments: ObservableArray<RoleAssignmentItemModel>;
    private roleDefinitions: SecurityRole[];
    private inheritanceOn: ObservableValue<boolean>;
    private inheritanceOff: ObservableValue<boolean>;
    private canManageRoles: ObservableValue<boolean>;

    constructor() {
        this.dirty = new ObservableValue(false);
        this.error = new ObservableValue(null);
        this.ready = new ObservableValue(false);
        this.roleAssignments = new ObservableArray();
        this.inheritanceOn = new ObservableValue(false);
        this.inheritanceOff = new ObservableValue<boolean>(true);
        this.canManageRoles = new ObservableValue<boolean>(true);
    }

    public initialize(roleAssignments: RoleAssignment[], roleDefinitions: SecurityRole[], managePermissions: IRoleManamentPermissions): void {
        this.setRoleAssignments(roleAssignments);
        this.roleDefinitions = roleDefinitions;
        this.canManageRoles.value = this.userCanManageRoleAssignments(managePermissions);
        this.ready.value = true;
    }

    public getError(): IReadonlyObservableValue<Error | null> {
        return this.error;
    }

    public setError(error: Error | null): void {
        this.error.value = error;
    }

    public getRoleDefinitions(): SecurityRole[] {
        return this.roleDefinitions;
    }

    public isInheritanceOn(): IReadonlyObservableValue<boolean> {
        return this.inheritanceOn;
    }

    public isInheritanceOff(): IReadonlyObservableValue<boolean> {
        return this.inheritanceOff;
    }

    public isDirty(): IReadonlyObservableValue<boolean> {
        return this.dirty;
    }

    public isReady(): IReadonlyObservableValue<boolean> {
        return this.ready;
    }

    public userCanManageRoles(): IReadonlyObservableValue<boolean> {
        return this.canManageRoles;
    }

    public getRoleAssignments(): IReadonlyObservableArray<RoleAssignmentItemModel> {
        return this.roleAssignments;
    }

    public setRoleAssignments(roleAssignments: RoleAssignment[]): void {
        this.originalRoleAssignments = roleAssignments;

        const roleAssignmentModels = roleAssignments.map(ra => new RoleAssignmentItemModel(ra));
        roleAssignmentModels.sort(this.sortRoleAssignments);

        this.roleAssignments.value = roleAssignmentModels;
        this.inheritanceOn.value = roleAssignments.some(ra => ra.access === RoleAccess.Inherited);
        this.inheritanceOff.value = !this.inheritanceOn.value;
        this.checkDirty();
    }

    public resetRoleAssignments(): void {
        this.setRoleAssignments(this.originalRoleAssignments);
    }

    public changeRoleAssignment(roleAssignment: RoleAssignmentItemModel, newRole: SecurityRole): void {
        roleAssignment.role = newRole;
        this.checkDirty();
    }

    public getChangedRoleAssignments(): UserRoleAssignmentRef[] {
        return this.roleAssignments.value
            .filter(ra => !ra.isDeleted().value && ra.isDirty().value)
            .map(ra => {
                return { userId: ra.userId, roleName: ra.role.name } as UserRoleAssignmentRef;
            });
    }

    public deleteRoleAssignment(roleAssignment: RoleAssignmentItemModel): void {
        roleAssignment.delete();
        this.checkDirty();
    }

    public getDeletedRoleAssignments(): string[] {
        return this.roleAssignments.value.filter(ra => ra.isDeleted().value).map(ra => ra.userId);
    }

    public addRoleAssignment(role: SecurityRole, identity: { id: string; name: string }): void {
        // See existing role assignment already exists
        const roleAssignment = this.roleAssignments.value.find(ra => ra.userId === identity.id);
        if (roleAssignment) {
            // Update existing role assignment
            this.changeRoleAssignment(roleAssignment, role);
        } else {
            const roleAssignmentModels = this.roleAssignments.value;

            // Add new role assignment
            roleAssignmentModels.push(
                new RoleAssignmentItemModel(
                    {
                        access: RoleAccess.Assigned,
                        role,
                        identity: { id: identity.id, displayName: identity.name } as IdentityRef
                    } as RoleAssignment,
                    true
                )
            );

            roleAssignmentModels.sort(this.sortRoleAssignments);
            this.roleAssignments.value = roleAssignmentModels;
            this.checkDirty();
        }
    }

    private sortRoleAssignments(model1: RoleAssignmentItemModel, model2: RoleAssignmentItemModel): number {
        return model1.displayName.localeCompare(model2.displayName, navigator.language);
    }

    private userCanManageRoleAssignments(managePermissions: IRoleManamentPermissions): boolean {
        const { canEdit, manageRolesPermission, userId } = managePermissions;

        if (canEdit === false) {
            return false;
        }

        if (userId && manageRolesPermission !== undefined) {
            // Find role assignment for the current user
            const roleAssignment = this.roleAssignments.value.find(ra => ra.userId === userId);
            if (roleAssignment) {
                // See the user has manage permissions or not
                return (roleAssignment.role.allowPermissions & manageRolesPermission) === manageRolesPermission;
            }
        }

        return true;
    }

    private checkDirty(): void {
        let isDirty = false;
        if (this.originalRoleAssignments.length !== this.roleAssignments.length) {
            isDirty = true;
        }

        if (!isDirty) {
            isDirty = this.roleAssignments.value.some(ra => ra.isDirty().value);
        }

        this.dirty.value = isDirty;
    }
}
